// client/src/pages/seo/QrCodeSpeisekarteDe.jsx
import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import SeoHeader from "../../components/seo/SeoHeader.jsx";
import SeoContactSection from "../../components/seo/SeoContactSection.jsx";

const CALLBACK_API_ORIGIN =
  typeof window !== "undefined" &&
  (window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1")
    ? "http://localhost:3030"
    : "";

const sections = [
  { id: "answer", label: "Was ist eine QR-Code-Speisekarte?" },
  { id: "create", label: "QR-Code für die Speisekarte erstellen: so geht es" },
  { id: "pdf", label: "PDF oder echte digitale Speisekarte?" },
  { id: "provider", label: "Worauf sollten Restaurants bei einem Anbieter achten?" },
  { id: "bestplace", label: "Wie funktioniert eine QR-Speisekarte mit Best Place?" },
  { id: "placement", label: "Wo sollte der QR-Code im Restaurant stehen?" },
  { id: "faq", label: "FAQ zur QR-Code-Speisekarte" },
];

function setMeta(name, content) {
  let tag = document.head.querySelector(`meta[name="${name}"]`);
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute("name", name);
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content);
}

function setCanonical(href) {
  let link = document.head.querySelector('link[rel="canonical"]');
  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    document.head.appendChild(link);
  }
  link.setAttribute("href", href);
}

export default function QrCodeMenuForRestaurantsDe() {
  useEffect(() => {
    document.documentElement.setAttribute("lang", "de");
    document.title = "QR-Code-Speisekarte für Restaurants erstellen | Best Place";
    setMeta(
      "description",
      "QR-Code-Speisekarte für Restaurants: digitales Menü erstellen, QR-Code teilen und Preise, Gerichte und Sprachen jederzeit online aktualisieren."
    );
    setCanonical("https://best-place.club/de/qr-code-speisekarte");
  }, []);

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  return (
    <main className="qrArticle">
      <SeoHeader
        lang="de"
        langLinks={{
          en: "/en/qr-code-menu-for-restaurants",
          de: "/de/qr-code-speisekarte",
          no: "/no/qr-kode-meny-for-restauranter",
        }}
      />

      <section className="qrArticle__hero">
        <div className="qrArticle__heroInner">
          <h1 className="qrArticle__heroTitle">
            <span>QR-Code-Speisekarte für Restaurants</span>
          </h1>

          <div className="qrArticle__topsection">
            <div className="qrArticle__topsection--img-wrapper">
              <div className="qrArticle__topsection--img"></div>
            </div>

            <div className="qrArticle__topsection--text-wrapper">
              <p className="seoHero__lead">
                Gäste scannen den QR-Code am Tisch und öffnen Ihre aktuelle
                Speisekarte direkt auf dem Smartphone. Das Menü läuft im Browser,
                ohne App und ohne Login.
              </p>

              <p className="seoHero__text">
                Der QR-Code bleibt derselbe, während Sie dahinter Preise,
                Gerichte, Fotos, Verfügbarkeiten und Sprachen online ändern. So
                müssen Tischaufsteller oder Karten nicht bei jeder Änderung neu
                gedruckt werden.
              </p>

            </div>
          </div>
        </div>
      </section>

      <section className="acquaintance">
        <div className="seoComparison">
          <div className="seoComparison__col seoComparison__col--highlight">
            <h2>Was Sie mit einer QR-Code-Speisekarte bekommen</h2>
            <ul className="qrArticle__block-li">
              <li className="text__seo-li">Menü direkt per Smartphone öffnen</li>
              <li className="text__seo-li">keine App für Gäste</li>
              <li className="text__seo-li">Preise und Gerichte sofort aktualisieren</li>
              <li className="text__seo-li">Fotos, Kategorien und Beschreibungen</li>
              <li className="text__seo-li">mehrere Sprachen möglich</li>
              <li className="text__seo-li">derselbe QR-Code nach Änderungen</li>
            </ul>
          </div>

          <div className="seoComparison__col">
            <h2>Was der QR-Code nicht ist</h2>
            <p>
              Der QR-Code selbst ist nur der Zugang. Entscheidend ist die Seite,
              die sich danach öffnet. Ein schlecht lesbares PDF hinter einem QR-Code
              ist technisch digital, aber für Gäste oft deutlich unbequemer als eine
              mobile Menüseite.
            </p>
          </div>
        </div>
      </section>

      <SeoContactSection lang="de" apiOrigin={CALLBACK_API_ORIGIN} />

      <section className="articls">
        <h2>Inhalt</h2>
        <nav className="qrArticle__nav" aria-label="Artikelnavigation">
          <ol className="qrArticle__navList">
            {sections.map((item, index) => (
              <li key={item.id} className="qrArticle__navItem">
                <button
                  type="button"
                  className="qrArticle__navButton"
                  onClick={() => scrollToSection(item.id)}
                >
                  <span className="qrArticle__navNumber">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  <span className="qrArticle__navLabel">{item.label}</span>
                </button>
              </li>
            ))}
          </ol>
        </nav>
      </section>

      <article>
        <section id="answer" className="acquaintance">
          <h2>Was ist eine QR-Code-Speisekarte?</h2>
          <p>
            Eine QR-Code-Speisekarte verbindet einen gedruckten QR-Code mit einer
            online erreichbaren Speisekarte. Der Gast scannt den Code mit der
            Smartphone-Kamera, tippt auf den Link und sieht das Menü im Browser.
          </p>
          <p>
            Für Restaurants liegt der eigentliche Vorteil nicht im QR-Code, sondern
            in der veränderbaren Zielseite. Wenn ein Preis angepasst, ein Gericht
            ausverkauft oder ein saisonales Angebot ergänzt wird, ändern Sie den
            Inhalt online. Der bereits gedruckte QR-Code kann unverändert bleiben.
          </p>
          <p>
            Genau deshalb sollte eine QR-Speisekarte nicht als einmal gedruckter
            Ersatz für Papier gedacht werden, sondern als laufend gepflegter
            digitaler Zugang zum Menü.
          </p>
        </section>

        <section id="create" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>QR-Code für die Speisekarte erstellen: so geht es</h2>
            <p>
              Wer einen QR-Code für die Speisekarte erstellen möchte, sollte nicht
              mit dem QR-Bild beginnen, sondern mit der Zielseite. Der Code ist nur
              so gut wie das Menü, das sich danach öffnet.
            </p>

            <div className="qrArticle__cardGrid">
              <div>
                <strong>1. Digitale Speisekarte anlegen</strong>
                <p>Kategorien, Gerichte, Preise, Beschreibungen und Fotos eintragen.</p>
              </div>
              <div>
                <strong>2. Gästeansicht prüfen</strong>
                <p>Das Menü auf einem echten Smartphone öffnen und testen.</p>
              </div>
              <div>
                <strong>3. QR-Code erzeugen</strong>
                <p>Den Code mit der stabilen Menü-URL verbinden.</p>
              </div>
              <div>
                <strong>4. Vor dem Druck scannen</strong>
                <p>Mit mehreren Geräten, Lichtverhältnissen und Abständen testen.</p>
              </div>
            </div>

            <p>
              Wenn Sie mit Best Place starten, können Sie zuerst das digitale Menü
              aufbauen und danach denselben Menü-Link über QR-Code, Website oder
              Nachricht teilen.
            </p>
          </div>
        </section>

        <section id="pdf" className="acquaintance">
          <h2>QR-Code mit PDF oder echte digitale Speisekarte?</h2>
          <div className="seoComparison">
            <div className="seoComparison__col">
              <h3>QR-Code mit PDF</h3>
              <ul className="qrArticle__block-li">
                <li className="text__seo-li">schnell eingerichtet</li>
                <li className="text__seo-li">auf kleinen Displays oft Zoomen nötig</li>
                <li className="text__seo-li">Änderungen erfordern meist eine neue Datei</li>
                <li className="text__seo-li">Produkte sind schwerer einzeln zu pflegen</li>
              </ul>
            </div>
            <div className="seoComparison__col seoComparison__col--highlight">
              <h3>Mobile digitale Speisekarte</h3>
              <ul className="qrArticle__block-li">
                <li className="text__seo-li">für Smartphones aufgebaut</li>
                <li className="text__seo-li">klare Kategorien und gut lesbare Preise</li>
                <li className="text__seo-li">Produkte einzeln aktualisierbar</li>
                <li className="text__seo-li">gleicher QR-Code trotz Inhaltsänderungen</li>
              </ul>
            </div>
          </div>
          <p>
            Wenn Ihre Karte fast nie geändert wird, kann ein PDF genügen. Wenn Sie
            Preise, Produkte, Verfügbarkeit, Fotos oder Sprachen regelmäßig pflegen,
            ist eine echte digitale Menüseite normalerweise flexibler.
          </p>
        </section>

        <section id="provider" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Worauf sollten Restaurants bei einem QR-Menü-Anbieter achten?</h2>
            <p>
              Die meisten Lösungen können einen QR-Code erzeugen. Für den Alltag
              sind deshalb andere Kriterien wichtiger als der Code selbst.
            </p>
            <ul className="qrArticle__block-li">
              <li className="text__seo-li"><strong>Mobile Nutzung:</strong> Menü ohne Zoomen und ohne App.</li>
              <li className="text__seo-li"><strong>Bearbeitung:</strong> Preise und Gerichte ohne Entwickler ändern.</li>
              <li className="text__seo-li"><strong>Stabile URL:</strong> der gedruckte QR-Code bleibt nutzbar.</li>
              <li className="text__seo-li"><strong>Sprachen:</strong> internationale Gäste können die Sprache wählen.</li>
              <li className="text__seo-li"><strong>Fotos und Kategorien:</strong> Produkte klar strukturieren.</li>
              <li className="text__seo-li"><strong>Testmöglichkeit:</strong> Gästeansicht vor dem Rollout prüfen.</li>
            </ul>
            <p>
              Wenn Sie zuerst grundsätzlich verschiedene Arten digitaler Menüs
              vergleichen möchten, lesen Sie unsere Seite zur{" "}
              <Link to="/de/digitale-speisekarte">digitalen Speisekarte für Restaurants</Link>.
            </p>
          </div>
        </section>

        <section id="bestplace" className="acquaintance">
          <h2>Wie funktioniert eine QR-Speisekarte mit Best Place?</h2>
          <div className="qrArticle__cardGrid">
            <div>
              <strong>Menü erstellen oder importieren</strong>
              <p>Kategorien, Namen, Preise, Beschreibungen und Bilder verwalten.</p>
            </div>
            <div>
              <strong>Online veröffentlichen</strong>
              <p>Die Speisekarte erhält eine eigene öffentlich erreichbare Seite.</p>
            </div>
            <div>
              <strong>QR-Code verwenden</strong>
              <p>Gäste öffnen dieselbe aktuelle Speisekarte am Tisch.</p>
            </div>
            <div>
              <strong>Später aktualisieren</strong>
              <p>Änderungen erscheinen online, ohne den QR-Code neu zu drucken.</p>
            </div>
          </div>

        </section>

        <section id="placement" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Wo sollte der QR-Code im Restaurant stehen?</h2>
            <p>
              Ein QR-Code funktioniert nur, wenn Gäste ihn im richtigen Moment
              sehen und problemlos scannen können. Typische Orte sind Tischaufsteller,
              kleine Karten auf dem Tisch, Theke, Fenster, Flyer oder Take-away-Verpackungen.
            </p>
            <p>
              Für Druckgröße, Material, Kontrast und Test vor der Produktion gibt
              es eine eigene Anleitung:{" "}
              <Link to="/de/qr-code-speisekarte-drucken">QR-Code für die Speisekarte drucken</Link>.
            </p>
            <p>
              Wenn Ihr Schwerpunkt auf dem Tischaufsteller selbst liegt, finden Sie
              hier weitere Hinweise:{" "}
              <Link to="/de/restaurant-qr-code-tischaufsteller">QR-Code-Tischaufsteller im Restaurant</Link>.
            </p>
          </div>
        </section>

        <section id="faq" className="acquaintance">
          <h2>FAQ zur QR-Code-Speisekarte</h2>
          <div className="seoFaq">
            <details>
              <summary>Brauchen Gäste eine App?</summary>
              <p>
                Nein. Eine QR-Speisekarte kann direkt im Smartphone-Browser geöffnet
                werden. Mehr dazu auf der Seite{" "}
                <Link to="/de/qr-code-speisekarte-ohne-app">QR-Code-Speisekarte ohne App</Link>.
              </p>
            </details>
            <details>
              <summary>Muss der QR-Code nach einer Preisänderung neu gedruckt werden?</summary>
              <p>
                Nicht, wenn der Code auf eine stabile digitale Menü-URL verweist.
                Dann ändern Sie nur den Inhalt der Speisekarte.
              </p>
            </details>
            <details>
              <summary>Kann ich Speisen und Getränke über denselben QR-Code zeigen?</summary>
              <p>
                Ja. In den meisten Restaurants ist ein Menü mit klaren Kategorien
                einfacher als mehrere verschiedene QR-Codes auf demselben Tisch.
              </p>
            </details>
            <details>
              <summary>Kann die Speisekarte mehrere Sprachen haben?</summary>
              <p>
                Ja. Eine digitale Karte kann mehrere Sprachversionen anbieten. Der
                Gast öffnet denselben Zugang und wählt anschließend seine Sprache.
              </p>
            </details>
            <details>
              <summary>Kann ich die QR-Speisekarte zuerst testen?</summary>
              <p>
                Ja. Testen Sie die Gästeansicht und den Scan-Ablauf vor dem Druck
                der finalen Tischmaterialien.
              </p>
            </details>
          </div>
        </section>

        <section className="articls qrArticle__related">
          <h2>Passende Themen</h2>
          <div className="seoCompareSmall">
            <p><Link to="/de/digitale-speisekarte">Digitale Speisekarte für Restaurants</Link></p>
            <p><Link to="/de/qr-code-speisekarte-ohne-app">QR-Speisekarte ohne App</Link></p>
            <p><Link to="/de/qr-code-speisekarte-drucken">QR-Code-Speisekarte drucken</Link></p>
            <p><Link to="/de/speisekarte-online-verwalten">Speisekarte online verwalten</Link></p>
          </div>
        </section>
      </article>
    </main>
  );
}
